import React, { useState, useEffect, useRef } from 'react';
import { FaPaperPlane, FaLinkedinIn, FaGithub, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { gsap } from '../../lib/gsap';
import { contactsData } from '../../data/contactsData';
import './Contacts.css';

function Contacts() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.contact-headline', {
        opacity: 0,
        y: 40,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.contact-headline',
          start: 'top 85%',
        },
      });

      gsap.from('.contact-info', {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.contact-info',
          start: 'top 82%',
        },
      });

      gsap.from('.contact-form', {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.contact-form',
          start: 'top 82%',
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      setStatus('Please fill in all fields.');
      return;
    }
    setStatus('Sending...');
    try {
      const res = await fetch('https://formsubmit.co/ajax/akshaygosavi621@gmail.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message,
          _subject: 'New message from your portfolio',
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('Message sent successfully!');
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      setStatus('Failed to send. Please try again or email me directly.');
    }
    setTimeout(() => setStatus(''), 6000);
  };

  const copyEmail = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(contactsData.email).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setEmailOpen(false);
    }, 1500);
  };

  const openGmail = () => {
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(contactsData.email)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setEmailOpen(false);
  };

  return (
    <section className="contact-section" id="contacts" ref={sectionRef}>
      <div className="contact-inner">
        <h2 className="contact-headline">LET'S TALK</h2>

        <div className="contact-grid">
          <div className="contact-info">
            <p className="contact-info-sub">
              Open for full-time web developer roles, contract engineering, and technical discussions.
            </p>

            <div className="contact-details">
              <div
                className="contact-detail-item"
                onClick={() => setEmailOpen(!emailOpen)}
                role="button"
                tabIndex={0}
              >
                <FaEnvelope className="contact-detail-icon" />
                <div className="contact-detail-text">
                  <span className="contact-detail-label">Email</span>
                  <span className="contact-detail-val">{contactsData.email}</span>
                </div>
                {emailOpen && (
                  <div className="email-popover">
                    {copied ? (
                      <span className="email-popover-copied">Copied!</span>
                    ) : (
                      <>
                        <button type="button" className="email-popover-btn" onClick={openGmail}>
                          Open Gmail
                        </button>
                        <button type="button" className="email-popover-btn" onClick={copyEmail}>
                          Copy address
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="contact-detail-item">
                <FaMapMarkerAlt className="contact-detail-icon" />
                <div className="contact-detail-text">
                  <span className="contact-detail-label">Location</span>
                  <span className="contact-detail-val">{contactsData.address}</span>
                </div>
              </div>
            </div>

            <div className="contact-socials">
              <a
                href={contactsData.linkedIn}
                target="_blank"
                rel="noreferrer"
                className="social-chip"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn />
              </a>
              <a
                href={contactsData.github}
                target="_blank"
                rel="noreferrer"
                className="social-chip"
                aria-label="GitHub"
              >
                <FaGithub />
              </a>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="c-name" className="input-label">Your name</label>
              <input
                type="text"
                id="c-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Akshay Gosavi"
                className="form-input"
                autoComplete="name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="c-email" className="input-label">Email address</label>
              <input
                type="email"
                id="c-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                className="form-input"
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="c-msg" className="input-label">Message</label>
              <textarea
                id="c-msg"
                rows="5"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me about your project..."
                className="form-input"
              />
            </div>

            {status && <div className="status-msg">{status}</div>}

            <button type="submit" className="cta-pill submit-btn">
              <span>Send message</span>
              <div className="icon-wrapper" aria-hidden="true">
                <FaPaperPlane style={{ fontSize: '0.78rem' }} />
              </div>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contacts;
